import { useState } from 'react';
import { Stepper, Button, Group, TextInput, PasswordInput, Code, Container, Center, Stack, Checkbox, Radio, Select, LoadingOverlay, Title, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { RegistrationFormValidations } from './validations/FormValidations';
import { AppDataService } from '../../services/AppDataService';
import useInfoModal from '../../hooks/useInfoModal';
import { MODAL_TYPE_ENUM } from '../../enums/CommonEnums';
import { useNavigate } from 'react-router-dom';

const RegistrationForm = () => {
  const [active, setActive] = useState(0);
  const openModal = useInfoModal();
  const [loading, setLoading] = useState(false);
  const [registerSucceeded, setRegisterSucceeded] = useState(false);
  const navigate = useNavigate();

  const form = useForm({
    initialValues: {
      companyName: '',
      companyEin: '',
      companyAddressLine1: '',
      companyAddressLine2: '',
      companyCity: '',
      companyState: '',
      companyZipCode: '',
      companyRole: '',
      firstName: '',
      lastName: '',
      title: '',
      email: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
      isOBRCMember: 'No'
    },

    validate: (values) => {
      if (active === 0) {
        return {
          companyName: RegistrationFormValidations.companyName(values.companyName),
          companyEin: RegistrationFormValidations.ein(values.companyEin),
          companyAddressLine1: RegistrationFormValidations.companyAddressLn1(values.companyAddressLine1),
          companyAddressLine2: RegistrationFormValidations.companyAddressLn2(values.companyAddressLine2),
          companyCity: RegistrationFormValidations.companyCity(values.companyCity),
          companyState: RegistrationFormValidations.companyState(values.companyState),
          companyZipCode: RegistrationFormValidations.companyZip(values.companyZipCode),
          companyRole: RegistrationFormValidations.companyRole(values.companyRole)
        };
      }

      if (active === 1) {
        return {
          firstName: RegistrationFormValidations.contactFirstName(values.firstName),
          lastName: RegistrationFormValidations.contactLastName(values.lastName),
          title: RegistrationFormValidations.contactTitle(values.title),
          email: RegistrationFormValidations.contactEmail(values.email),
          phoneNumber: RegistrationFormValidations.contactPhoneNumber(values.phoneNumber),
          password: RegistrationFormValidations.contactPassword(values.password),
          confirmPassword: RegistrationFormValidations.confirmContactPassword(values.confirmPassword, values),
        };
      }

      return {};
    },
  });

  const nextStep = () =>
    setActive((current) => {
      if (form.validate().hasErrors) {
        return current;
      }
      return current < 2 ? current + 1 : current;
    });

  const prevStep = () => setActive((current) => (current > 0 ? current - 1 : current));

  const handleRegisterUser = async () => {
    try {
      setLoading(true);
      const webResp = await AppDataService.RegisterUser({...form.values, isOBRCMember: form.values.isOBRCMember === "Yes"});
      const responseData = webResp.getResponse();
  
      if(webResp.hasError()) {
        setRegisterSucceeded(false);
        setLoading(false);
        return openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, responseData.error.message);
      } 
      else {
        openModal(MODAL_TYPE_ENUM.GENERAL_SUCCESS, 'Your profile has been registered. You may now login.');
        setRegisterSucceeded(true);
      }
      
      setLoading(false);
    }
    catch(err) {
      openModal(MODAL_TYPE_ENUM.GENERAL_FAILURE, err.message);
      setLoading(false);
      setRegisterSucceeded(false);
    }
  }

  return (
    <Stack justify='center' align='stretch' mx={75} mt={10}>
        <Stepper active={active} breakpoint="sm">
          <Stepper.Step label="Company Info" description="Let's start with some basic information about your company">
            <Stack>
                <TextInput withAsterisk label="Company Name" placeholder="Company Name" {...form.getInputProps('companyName')} />
                <TextInput withAsterisk label="EIN" placeholder="EIN" {...form.getInputProps('companyEin')} />
                <TextInput withAsterisk label="Company Address Line 1" placeholder="Company Address Line 1" {...form.getInputProps('companyAddressLine1')} />
                <TextInput label="Company Address Line 2" placeholder="Company Address Line 2" {...form.getInputProps('companyAddressLine2')} />
                <TextInput withAsterisk label="Company City" placeholder="Company City" {...form.getInputProps('companyCity')} />
                <TextInput withAsterisk label="Company State" placeholder="Company State" {...form.getInputProps('companyState')} />
                <TextInput withAsterisk label="Company ZipCode" placeholder="Company ZipCode" {...form.getInputProps('companyZipCode')} />
                <Select
                  withAsterisk
                  withinPortal={true}
                  label='What is your company role?'
                  dropdownPosition='bottom'
                  placeholder='Company Role'
                  {...form.getInputProps('companyRole')}
                  data={[
                    {value: 'Beverage Distributor', label: 'Beverage Distributor' },
                    {value: 'Beverage Importer', label: 'Beverage Importer' },
                    {value: 'Beverage Manufacturer', label: 'Beverage Manufacturer' },
                  ]}
                />
                <Radio.Group
                  name="obrcMember"
                  label="Is your business a member of OBRC?"
                  withAsterisk
                  {...form.getInputProps('isOBRCMember')}
                >
                  <Group mt="xs">
                    <Radio value='Yes' label="Yes" />
                    <Radio value='No' label="No" />
                  </Group>
                </Radio.Group>
              </Stack>
          </Stepper.Step>

          <Stepper.Step label="Contact Info" description="Now let's gather some login details so we can create your account">
            <Stack>
              <TextInput withAsterisk label="Contact First Name" placeholder="Contact First Name" {...form.getInputProps('firstName')} />
              <TextInput withAsterisk label="Contact Last Name" placeholder="Contact Last Name" {...form.getInputProps('lastName')} />
              <TextInput label="Contact Title" placeholder="Contact Title" {...form.getInputProps('title')} />
              <TextInput withAsterisk label="Contact Email" placeholder="Contact Email" {...form.getInputProps('email')} />
              <TextInput withAsterisk label="Contact Phone Number" placeholder="Contact Phone Number" {...form.getInputProps('phoneNumber')} />
              <PasswordInput withAsterisk label="Password" placeholder="Password" {...form.getInputProps('password')} />
              <PasswordInput withAsterisk label="Confirm Password" placeholder="Confirm Password" {...form.getInputProps('confirmPassword')} />
            </Stack>
          </Stepper.Step>

          <Stepper.Completed>
            <LoadingOverlay visible={loading} overlayBlur={2} />
            { registerSucceeded ? (
                <Stack align='center' spacing='xs' mt={25}>
                  <Text>Your account was created successfully!</Text>
                  <Button disabled={loading} onClick={() => navigate('/authenticate')}>Go To Login Screen</Button>
                </Stack>
              ) : (
                <>
                  <Stack align='center' spacing='xs'>
                    <Title fz={20}>Company Information</Title>
                    <Group>
                      <Text fw={600}>Company Name</Text>
                      <Text>{form.values.companyName}</Text>
                    </Group>
                    <Group>
                      <Text fw={600}>Company EIN</Text>
                      <Text>{form.values.companyEin}</Text>
                    </Group>
                    <Group>
                      <Text fw={600}>Company Address Line 1</Text>
                      <Text>{form.values.companyAddressLine1}</Text>
                    </Group>
                    <Group>
                      <Text fw={600}>Company Address Line 2</Text>
                      <Text>{form.values.companyAddressLine2}</Text>
                    </Group>
                    <Group>
                      <Text fw={600}>Company City</Text>
                      <Text>{form.values.companyCity}</Text>
                    </Group>
                    <Group>
                      <Text fw={600}>Company State</Text>
                      <Text>{form.values.companyState}</Text>
                    </Group>
                    <Group>
                      <Text fw={600}>Company Zip Code</Text>
                      <Text>{form.values.companyZipCode}</Text>
                    </Group>
                    <Group>
                      <Text fw={600}>Company Role</Text>
                      <Text>{form.values.companyRole}</Text>
                    </Group>
                    <Group>
                      <Text fw={600}>OBRC Member?</Text>
                      <Text>{form.values.isOBRCMember}</Text>
                    </Group>
                  </Stack>
                  <Stack align='center' spacing='xs' mt={25}>
                    <Title fz={20}>Contact Information</Title>
                    <Group>
                      <Text fw={600}>First Name</Text>
                      <Text>{form.values.firstName}</Text>
                    </Group>
                    <Group>
                      <Text fw={600}>Last Name</Text>
                      <Text>{form.values.lastName}</Text>
                    </Group>
                    <Group>
                      <Text fw={600}>Title</Text>
                      <Text>{form.values.title}</Text>
                    </Group>
                    <Group>
                      <Text fw={600}>Company Email</Text>
                      <Text>{form.values.email}</Text>
                    </Group>
                    <Group>
                      <Text fw={600}>Company Phone Number</Text>
                      <Text>{form.values.phoneNumber}</Text>
                    </Group>
                  </Stack>
                  <Stack align='center' spacing='xs' mt={25}>
                    <Button onClick={handleRegisterUser}>Create Account</Button>
                  </Stack>
                </>
              )
            }
          </Stepper.Completed>
        </Stepper>

        <Group position="center" mt="xl">
          {active !== 0 && (
            <Button variant="default" onClick={prevStep} disabled={registerSucceeded}>
              Back
            </Button>
          )}
          {active !== 2 && <Button onClick={nextStep}>Next step</Button>}
        </Group>
    </Stack>
  );
}

export default RegistrationForm;